/**
 * DiscussExecutionModal — wide debug-chat modal for the "Discuss this
 * Execution" feature.
 *
 * Mounted at `AppShell` level, driven by `useUIStore.discussModal`. Renders
 * nothing when `discussModal.open === false`.
 *
 * Architecture: two-component pattern.
 *
 *   1. `DiscussExecutionModal` — outer gate. Subscribes to the UI store,
 *      computes `isOpen`, and renders null or `<DiscussExecutionModalContent>`.
 *      Holds NO hooks that depend on `executionId` so there is no null-dep
 *      churn while the modal is closed.
 *
 *   2. `DiscussExecutionModalContent` — inner implementation. Only mounts
 *      when there is a real, non-null `executionId`, so every hook inside
 *      always runs with a stable, meaningful value. This prevents the rapid
 *      null→real state transitions that caused React error #185 when the
 *      prior single-component design called `useDiscussContext(null)` while
 *      closed and then `useDiscussContext(executionId)` the moment the modal
 *      opened.
 *
 * Layout (mirrors `AgentSessionModal` chrome):
 *
 *   ┌───────────────────────── header ────────────────────────────────┐
 *   │ Discuss execution · exec_xxx               status pill · close  │
 *   ├──────────────┬──────────────────────────────────────────────────┤
 *   │              │                                                  │
 *   │   sidebar    │             message thread                       │
 *   │  (context)   │                                                  │
 *   │              │                                                  │
 *   │              ├──────────────────────────────────────────────────┤
 *   │              │             message input                        │
 *   └──────────────┴──────────────────────────────────────────────────┘
 *
 * Chat state lives in `useDiscussStore` keyed by `(executionId, agentType)`
 * so closing and reopening the modal for the same execution resumes the
 * conversation. The persisted agent session id (`sessionIdByExecution`) means
 * each agent's conversation appears in the Sessions Browser.
 *
 * Currently only the `'debugger'` agent (automation_debugger) ships in the
 * UI; the agentType plumbing is in place so an agent dropdown can land in a
 * follow-up without another refactor.
 */

import { useCallback, useRef, useState } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import { RotateCcw, Workflow, X } from 'lucide-react'

import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/shared/components/ui/dialog'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/shared/components/ui/tooltip'
import { Button } from '@/shared/components/ui/button'
import { useUIStore } from '@/shared/stores/ui-store'
import { useAuthStore } from '@/features/auth'

import { useDiscussContext } from '@/features/queue/hooks/useDiscussContext'
import {
  selectMessageCount,
  useDiscussStore,
  type AgentType,
} from '@/features/queue/stores/discuss-store'
import { getDiscussAgentId } from '@/features/agents/discussAgent'
import { generateSessionId } from '@/features/session/store'

import { DiscussContextSidebar } from './DiscussContextSidebar'
import { DiscussMessageThread } from './DiscussMessageThread'
import {
  DiscussMessageInput,
  type DiscussMessageInputHandle,
} from './DiscussMessageInput'

/**
 * The default (and currently only) agent type shown in the modal. When
 * the agent dropdown lands, this will be initialised from the
 * `discussModal` UI store slice and updated by the dropdown onChange.
 */
const DEFAULT_AGENT_TYPE: AgentType = 'debugger'

/** Truncate a long id for display in the header. */
function shortId(value: string): string {
  if (value.length <= 14) return value
  return `…${value.slice(-12)}`
}

// ── Outer gate ────────────────────────────────────────────────────────────────

/**
 * Outer wrapper — subscribes to `useUIStore.discussModal` and renders the
 * inner content only when the modal is open and an executionId is present.
 * Holds no hooks that depend on executionId so there is no null-dep churn
 * while the modal is closed.
 */
export function DiscussExecutionModal(): ReactNode {
  const discussModal = useUIStore((s) => s.discussModal)
  const closeDiscussModal = useUIStore((s) => s.closeDiscussModal)
  const operatorId = useAuthStore((state) => state.operator?.id)

  const { executionId, attentionItemId } = discussModal
  const isOpen = discussModal.open && executionId !== null

  if (!isOpen || !executionId) return null

  return (
    <DiscussExecutionModalContent
      executionId={executionId}
      attentionItemId={attentionItemId}
      closeDiscussModal={closeDiscussModal}
      operatorId={operatorId ?? undefined}
    />
  )
}

// ── Inner content ─────────────────────────────────────────────────────────────

interface DiscussExecutionModalContentProps {
  /** Always a real, non-null execution id — guaranteed by the outer gate. */
  executionId: string
  attentionItemId: string | null
  closeDiscussModal: () => void
  operatorId?: string
}

/**
 * Inner implementation — only mounts when `executionId` is a real string.
 * Every hook here (useDiscussContext, useDiscussStore selectors, etc.) runs
 * with a stable, non-null executionId from the very first render.
 */
function DiscussExecutionModalContent({
  executionId,
  attentionItemId,
  closeDiscussModal,
  operatorId,
}: DiscussExecutionModalContentProps): ReactNode {
  // Local agent selection. Default to 'debugger'; the dropdown that
  // lets the operator switch agents will write into this state.
  const [agentType, setAgentType] = useState<AgentType>(DEFAULT_AGENT_TYPE)
  // Silence the unused-setter lint until the dropdown lands. Marking
  // it intentional preserves the wiring so the follow-up PR is a
  // one-line dropdown insertion.
  void setAgentType

  const inputRef = useRef<DiscussMessageInputHandle | null>(null)

  const { context, status, perSessionErrors, executionError, contextText } =
    useDiscussContext(executionId, {
      // Thread the AttentionItem id so the markdown context envelope
      // can reference it (was hardcoded null pre-3701h).
      attentionItemId,
    })

  // Per-(executionId, agentType) message count — drives both the footer
  // label and the Restart button's disabled state.
  const messageCount = useDiscussStore((s) =>
    selectMessageCount(s, executionId, agentType),
  )

  // Store actions for the Restart-conversation flow.
  const clearMessages = useDiscussStore((s) => s.clearMessages)
  const resetContextSent = useDiscussStore((s) => s.resetContextSent)
  const setStreamingState = useDiscussStore((s) => s.setStreamingState)
  const setError = useDiscussStore((s) => s.setError)
  const setSessionIdForExecution = useDiscussStore(
    (s) => s.setSessionIdForExecution,
  )

  const handleOpenChange = useCallback(
    (open: boolean) => {
      if (!open) closeDiscussModal()
    },
    [closeDiscussModal],
  )

  // Restart-conversation handler (Task 3701h G.2).
  //
  // Clears the current (executionId, agentType) conversation, mints a
  // fresh session id, resets the context-sent flag so the next message
  // re-prepends the context envelope, aborts any in-flight SSE stream,
  // clears any error, and focuses the input. Per-agent — the other
  // agent's conversation against the same approval is left untouched.
  const handleRestart = useCallback(() => {
    if (messageCount === 0) return
    if (
      !window.confirm(
        "Clear this conversation and start fresh? The other agent's conversation against this approval will not be affected.",
      )
    ) {
      return
    }

    // Abort any in-flight stream before mutating store state so the SSE
    // callback below doesn't race against the cleared slice.
    inputRef.current?.abortStream()

    clearMessages(executionId, agentType)
    resetContextSent(executionId, agentType)
    setStreamingState(executionId, agentType, 'idle')
    setError(executionId, agentType, null)
    setSessionIdForExecution(executionId, agentType, generateSessionId())

    inputRef.current?.focus()
  }, [
    executionId,
    agentType,
    messageCount,
    clearMessages,
    resetContextSent,
    setStreamingState,
    setError,
    setSessionIdForExecution,
  ])

  // Voiding lint warnings about unused context bindings — these are
  // present so future expansions of the modal can read them without
  // changing the hook signature.
  void context

  const debugAgentId = getDiscussAgentId()
  const restartDisabled = messageCount === 0

  return (
    <Dialog open onOpenChange={handleOpenChange}>
      <DialogContent style={contentStyle} className="discuss-execution-modal" data-testid="discuss-execution-modal">
        <div style={styles.layout}>
          {/* Header */}
          <div style={styles.header}>
            <div style={styles.headerLeft}>
              <div style={styles.headerAvatar}>
                <Workflow size={18} />
              </div>
              <div style={styles.headerText}>
                <DialogTitle style={styles.title}>
                  Discuss execution
                </DialogTitle>
                <DialogDescription style={styles.subtitle} asChild>
                  <div style={styles.metaStrip}>
                    <span style={styles.metaAgent}>
                      {agentType === 'debugger' ? 'Debugger' : 'App Expert'}
                    </span>
                    <span style={styles.metaDot} aria-hidden="true">·</span>
                    <span style={styles.metaMono}>{shortId(executionId)}</span>
                    {attentionItemId && (
                      <>
                        <span style={styles.metaDot} aria-hidden="true">·</span>
                        <span style={styles.metaItem}>
                          from queue item
                        </span>
                      </>
                    )}
                  </div>
                </DialogDescription>
              </div>
            </div>
            <div style={styles.headerRight}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleRestart}
                    disabled={restartDisabled}
                    aria-label="Restart conversation"
                    data-testid="discuss-restart-button"
                  >
                    <RotateCcw size={16} aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  Restart conversation — clears messages and starts a fresh session with this agent
                </TooltipContent>
              </Tooltip>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={closeDiscussModal}
                    aria-label="Close"
                  >
                    <X size={16} aria-hidden="true" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent>Close (Esc)</TooltipContent>
              </Tooltip>
            </div>
          </div>

          {/* Body — sidebar + chat column */}
          <div style={styles.body}>
            <DiscussContextSidebar
              status={status}
              context={context}
              perSessionErrors={perSessionErrors}
              executionError={executionError}
            />

            <div style={styles.chatColumn}>
              <DiscussMessageThread
                executionId={executionId}
                agentType={agentType}
                emptyHint={
                  contextText
                    ? 'Context loaded. Ask anything about this execution.'
                    : 'Loading context — once it finishes you can ask questions.'
                }
              />
              <DiscussMessageInput
                ref={inputRef}
                executionId={executionId}
                agentType={agentType}
                debugAgentId={debugAgentId}
                contextText={contextText}
                operatorId={operatorId}
              />
            </div>
          </div>

          {/* Footer */}
          <div style={styles.footer}>
            <span style={styles.footerLeft}>
              {messageCount > 0
                ? `${messageCount} turn${messageCount === 1 ? '' : 's'}`
                : 'No conversation yet'}
              {status === 'partial' && ' · loading more context…'}
              {status === 'loading' && ' · loading context…'}
            </span>
            <span style={styles.footerRight}>
              Agent: <code style={styles.footerCode}>{debugAgentId}</code>
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}

// ── Styles ───────────────────────────────────────────────────────────────────

const contentStyle: CSSProperties = {
  position: 'fixed',
  left: '50%',
  top: '50%',
  zIndex: 50,
  transform: 'translate(-50%, -50%)',
  width: '92vw',
  maxWidth: '1180px',
  height: '92vh',
  maxHeight: '96vh',
  padding: 0,
  display: 'flex',
  flexDirection: 'column',
  gap: 0,
  borderRadius: 'var(--radius-lg)',
  border: '1px solid var(--border-default)',
  background: 'var(--bg-secondary)',
  boxShadow: 'var(--shadow-lg)',
  overflow: 'hidden',
}

const styles: Record<string, CSSProperties> = {
  layout: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    width: '100%',
    minHeight: 0,
  },
  header: {
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '16px 20px 14px 22px',
    borderBottom: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    flexShrink: 0,
  },
  headerLeft: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '14px',
    minWidth: 0,
    flex: 1,
  },
  headerAvatar: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '38px',
    height: '38px',
    borderRadius: '50%',
    background:
      'linear-gradient(135deg, color-mix(in srgb, var(--accent-primary) 28%, transparent) 0%, color-mix(in srgb, var(--accent-primary) 12%, transparent) 100%)',
    color: 'var(--accent-primary)',
    flexShrink: 0,
    marginTop: '1px',
  },
  headerText: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
    minWidth: 0,
  },
  title: {
    fontSize: '17px',
    fontWeight: 600,
    color: 'var(--text-primary)',
    margin: 0,
    lineHeight: 1.25,
    letterSpacing: '-0.01em',
  },
  subtitle: {
    margin: 0,
  },
  metaStrip: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: '6px',
    fontSize: '11.5px',
    color: 'var(--text-muted)',
    lineHeight: 1.3,
    marginTop: '2px',
  },
  metaAgent: {
    fontWeight: 500,
    color: 'var(--text-secondary)',
  },
  metaItem: {
    color: 'var(--text-muted)',
  },
  metaMono: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  metaDot: {
    color: 'var(--text-faint)',
  },
  headerRight: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    flexShrink: 0,
  },
  body: {
    flex: 1,
    minHeight: 0,
    display: 'grid',
    gridTemplateColumns: 'minmax(260px, 2fr) minmax(0, 3fr)',
  },
  chatColumn: {
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    background: 'var(--bg-primary)',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: '12px',
    padding: '10px 20px',
    borderTop: '1px solid var(--border-subtle)',
    background: 'var(--bg-secondary)',
    flexShrink: 0,
  },
  footerLeft: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  footerRight: {
    fontSize: '11px',
    color: 'var(--text-muted)',
  },
  footerCode: {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '10.5px',
    background: 'var(--bg-tertiary)',
    padding: '1px 4px',
    borderRadius: '3px',
  },
}

export default DiscussExecutionModal
